import { createFileRoute } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { Layout } from "@/components/site/Layout";

export const Route = createFileRoute("/risk-disclaimer")({
  head: () => ({
    meta: [
      { title: "Risk Disclaimer | XAU Pro Academy" },
      {
        name: "description",
        content:
          "XAU Pro Academy is an educational platform. Read our full risk disclaimer and understand the educational nature of our market analysis.",
      },
      { property: "og:title", content: "Risk Disclaimer | XAU Pro Academy" },
      {
        property: "og:description",
        content:
          "XAU Pro Academy is an educational platform. Read our full risk disclaimer and understand the educational nature of our market analysis.",
      },
      { property: "og:url", content: "https://xauproacademy.com/risk-disclaimer" },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/risk-disclaimer" }],
  }),
  component: RiskDisclaimerPage,
});

function RiskDisclaimerPage() {
  return (
    <Layout>
      <section className="container mx-auto max-w-3xl px-4 py-16">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
          <ShieldAlert className="h-7 w-7" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold md:text-4xl">
          Risk Disclaimer & Educational Notice
        </h1>
        <p className="mt-3 text-muted-foreground">
          Please read this disclaimer carefully before using XAU Pro Academy.
        </p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-foreground/90">
          <section>
            <h2 className="font-display text-lg font-semibold">
              1. Educational Platform
            </h2>
            <p className="mt-2 text-muted-foreground">
              XAU Pro Academy is a{" "}
              <strong className="text-foreground">
                financial technology and quantitative market research software
              </strong>{" "}
              developed by Orion AI Technologies - F.Z.C. All desk observations,
              alerts, and historical case studies are the automated output of
              algorithmic market structure models governed under the Orion AI
              framework (including OTAS-01, OSAS-01, and all current, upgraded,
              and future proprietary quantitative or research models) and
              proprietary data analysis systems, provided for educational and
              analytical purposes only. These outputs help members identify where
              real liquidity sits in the Gold (XAU/USD) market. This is an
              educational research service, not a financial advisory service.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              2. Not Financial Advice
            </h2>
            <p className="mt-2 text-muted-foreground">
              All market analysis, trade case studies, and content shared through
              XAU Pro Academy is the automated output of the proprietary
              algorithmic models governed under the Orion AI framework,
              together with proprietary data analysis systems, published
              for{" "}
              <strong className="text-foreground">educational and training purposes only</strong>.
              Nothing on this platform constitutes financial, investment, legal, or
              tax advice, and no content should be construed as a solicitation,
              recommendation, or offer to buy or sell any financial instrument.
              We are not registered investment advisers and do not
              manage client funds.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              3. Trading Risk
            </h2>
            <p className="mt-2 text-muted-foreground">
              Trading foreign exchange and precious metals on margin carries a
              <strong className="text-foreground"> high level of risk</strong> and may
              not be suitable for all investors. The high degree of leverage can work
              against you as well as for you. You may sustain losses in excess of your
              initial deposit. Past performance is not indicative of future results.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              4. Personal Responsibility
            </h2>
            <p className="mt-2 text-muted-foreground">
              You are solely responsible for your own trading decisions, and you
              agree not to rely on any platform content as the basis for a trading
              or investment decision. Before acting
              on any analysis or case study shared through XAU Pro Academy, you should
              carefully consider your investment objectives, level of experience, and
              risk appetite, and seek independent advice from a licensed professional
              if necessary.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              5. No Guarantees
            </h2>
            <p className="mt-2 text-muted-foreground">
              XAU Pro Academy makes no representation or guarantee regarding the
              profitability of any trade, strategy, or methodology discussed.
              Hypothetical performance results have many inherent limitations and
              should not be relied upon for any trading decision.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              6. Membership Terms
            </h2>
            <p className="mt-2 text-muted-foreground">
              By becoming a member of XAU Pro Academy, you acknowledge that you have
              read, understood, and agreed to this disclaimer in full. Memberships
              grant access to educational content and analysis only, they do not
              grant any form of investment management or advisory relationship.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              7. Model-Based Performance Record
            </h2>
            <p className="mt-2 text-muted-foreground">
              The published performance record is computed from the fixed rules
              of the proprietary models governed under the Orion AI framework
              (including OTAS-01 and OSAS-01, together with all current,
              upgraded, and future models) and expressed in risk units (R). It is a
              rule-based record, not the statement of any individual account. It
              does not reflect your broker spread, commission, slippage, swap,
              execution latency, position sizing, or the timing of your own
              entries and exits. Your results will differ, and may differ
              materially.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              8. Market Data and Delivery Dependencies
            </h2>
            <p className="mt-2 text-muted-foreground">
              Prices, candles, calendars, and alerts rely on independent data
              vendors, hosting providers, and messaging networks outside our
              control. Quotes may be delayed, revised, or interrupted, and
              notifications may arrive late or not at all. Our prices are
              reference data and may not match the quotes of your broker. Never
              rely on a single notification or data point to manage open risk.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              9. Independence and Conflicts of Interest
            </h2>
            <p className="mt-2 text-muted-foreground">
              XAU Pro Academy is operated by Orion AI Technologies - F.Z.C
              (Ajman Free Zone License No. 58070) and is
              financially independent. We receive no commission, rebate, referral
              fee, or volume-based compensation from any broker, liquidity
              provider, or proprietary trading firm. Our revenue comes only from
              memberships, so our model carries no incentive tied to how often
              or how large you trade.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              10. Official Channels Only
            </h2>
            <p className="mt-2 text-muted-foreground">
              We never manage funds, request account credentials, ask for
              deposits to trading accounts, or contact members through private
              messages to offer setups. Any such request does not come from us.
              Official communication is limited to this website, your member
              dashboard, and support@xauproacademy.com.
            </p>
          </section>
        </div>
      </section>
    </Layout>
  );
}
